import { useState } from 'react';
import { FEATHER_OPTIONS, FEATHER_MAP } from './featherIcons';

/**
 * Available icons for nodes — curated set of emoji that work well as
 * file/folder icons across platforms.
 */
const ICON_OPTIONS = [
  // Documents & Notes
  '\u{1F4C4}', // 📄 page facing up
  '\u{1F4DD}', // 📝 memo
  '\u{1F4C3}', // 📃 page with curl
  '\u{1F4CB}', // 📋 clipboard
  '\u{1F5D2}', // 🗒 spiral notepad
  '\u{1F4D1}', // 📑 bookmark tabs
  '\u{1F4C1}', // 📁 folder
  '\u{1F4C2}', // 📂 open folder
  '\u{1F5C2}', // 🗂 card index dividers

  // Stars & Favorites
  '\u{2B50}',   // ⭐ star
  '\u{1F31F}', // 🌟 glowing star
  '\u{1F4AB}', // 💫 dizzy
  '\u{2728}',   // ✨ sparkles

  // Status & Priority
  '\u{1F534}', // 🔴 red circle
  '\u{1F7E0}', // 🟠 orange circle
  '\u{1F7E1}', // 🟡 yellow circle
  '\u{1F7E2}', // 🟢 green circle
  '\u{1F535}', // 🔵 blue circle
  '\u{1F7E3}', // 🟣 purple circle
  '\u{26AA}',   // ⚪ white circle
  '\u{26AB}',   // ⚫ black circle
  '\u{2705}',   // ✅ check mark
  '\u{274C}',   // ❌ cross mark
  '\u{26A0}',   // ⚠ warning
  '\u{203C}',   // ‼ double exclamation

  // Ideas & Thinking
  '\u{1F4A1}', // 💡 light bulb
  '\u{1F9E0}', // 🧠 brain
  '\u{1F3AF}', // 🎯 bullseye
  '\u{1F4A5}', // 💥 collision
  '\u{1F525}', // 🔥 fire
  '\u{1F4A7}', // 💧 droplet

  // Communication
  '\u{1F4AC}', // 💬 speech balloon
  '\u{1F4E7}', // 📧 e-mail
  '\u{1F514}', // 🔔 bell
  '\u{1F4AD}', // 💭 thought balloon

  // Objects
  '\u{1F4C5}', // 📅 calendar
  '\u{1F4C8}', // 📈 chart increasing
  '\u{1F4C9}', // 📉 chart decreasing
  '\u{1F4CA}', // 📊 bar chart
  '\u{1F4CC}', // 📌 pushpin
  '\u{1F4CD}', // 📍 round pushpin
  '\u{1F4CE}', // 📎 paperclip
  '\u{1F516}', // 🔖 bookmark
  '\u{1F3F7}', // 🏷 label
  '\u{1F50D}', // 🔍 magnifying glass
  '\u{1F511}', // 🔑 key
  '\u{1F512}', // 🔒 lock
  '\u{1F513}', // 🔓 unlock
  '\u{1F6A9}', // 🚩 triangular flag
  '\u{1F3F4}', // 🏴 black flag
  '\u{1F389}', // 🎉 party popper

  // Nature
  '\u{1F33F}', // 🌿 herb
  '\u{1F340}', // 🍀 four leaf clover
  '\u{1F33B}', // 🌻 sunflower
  '\u{1F33C}', // 🌼 blossom
  '\u{1F338}', // 🌸 cherry blossom

  // Tech / Code
  '\u{1F4BB}', // 💻 laptop
  '\u{1F4F1}', // 📱 mobile
  '\u{1F4BE}', // 💾 floppy disk
  '\u{1F5A5}', // 🖥 desktop
  '\u{1F6E0}', // 🛠 tools
  '\u{2699}',   // ⚙ gear
  '\u{1F527}', // 🔧 wrench

  // Time & Schedule
  '\u{1F4C6}', // 📆 tear-off calendar
  '\u{1F5D3}', // 🗓 spiral calendar
  '\u{23F0}',   // ⏰ alarm clock
  '\u{231A}',   // ⌚ watch
  '\u{23F3}',   // ⏳ hourglass
  '\u{1F551}', // 🕑 clock face

  // Weather
  '\u{2600}',   // ☀ sun
  '\u{26C5}',   // ⛅ sun behind cloud
  '\u{2601}',   // ☁ cloud
  '\u{1F327}', // 🌧 rain cloud
  '\u{26C8}',   // ⛈ thunderstorm
  '\u{1F328}', // 🌨 snow cloud
  '\u{1F32B}', // 🌫 fog
  '\u{1F4A8}', // 💨 wind

  // Health & Wellness
  '\u{1F3C3}', // 🏃 runner
  '\u{1F6B4}', // 🚴 person biking
  '\u{1F3CB}', // 🏋 person lifting weights
  '\u{1F9D8}', // 🧘 person in lotus
  '\u{1F48A}', // 💊 pill
  '\u{1F4AA}', // 💪 flexed biceps
  '\u{1F9D7}', // 🧗 person climbing

  // Food & Drink
  '\u{2615}',   // ☕ hot beverage
  '\u{1F375}', // 🍵 teacup
  '\u{1F37A}', // 🍺 beer
  '\u{1F377}', // 🍷 wine glass
  '\u{1F354}', // 🍔 hamburger
  '\u{1F35C}', // 🍜 steaming bowl
  '\u{1F368}', // 🍨 ice cream
  '\u{1F34E}', // 🍎 red apple

  // Travel & Transport
  '\u{2708}',   // ✈ airplane
  '\u{1F697}', // 🚗 automobile
  '\u{1F689}', // 🚉 station
  '\u{1F3F0}', // 🏰 castle
  '\u{1F3D6}', // 🏖 beach with umbrella
  '\u{1F3D4}', // 🏔 snow-capped mountain
  '\u{1F5FA}', // 🗺 world map
  '\u{1F3E8}', // 🏨 hotel

  // Money & Finance
  '\u{1F4B0}', // 💰 money bag
  '\u{1F4B3}', // 💳 credit card
  '\u{1F4B5}', // 💵 dollar banknote
  '\u{1F4B9}', // 💹 chart increasing with yen
  '\u{1F4B8}', // 💸 money with wings

  // Home & Living
  '\u{1F3E0}', // 🏠 house
  '\u{1F3E1}', // 🏡 house with garden
  '\u{1F6CB}', // 🛋 couch and lamp
  '\u{1F6CF}', // 🛏 bed
  '\u{1F4A4}', // 💤 sleeping symbol

  // Books & Learning
  '\u{1F4DA}', // 📚 books
  '\u{1F4D6}', // 📖 open book
  '\u{1F4D3}', // 📓 notebook
  '\u{1F393}', // 🎓 graduation cap
  '\u{1F4AF}', // 💯 hundred points

  // Shopping
  '\u{1F6CD}', // 🛍 shopping bags
  '\u{1F6D2}', // 🛒 shopping cart

  // Pets & Animals
  '\u{1F431}', // 🐱 cat face
  '\u{1F436}', // 🐶 dog face
  '\u{1F438}', // 🐸 frog face
  '\u{1F426}', // 🐦 bird
  '\u{1F98B}', // 🦋 butterfly

  // Misc
  '\u{1F3B5}', // 🎵 music note
  '\u{1F3A8}', // 🎨 artist palette
  '\u{1F3AC}', // 🎬 clapper board
  '\u{1F4F7}', // 📷 camera
  '\u{1F48E}', // 💎 gem stone
  '\u{1F381}', // 🎁 gift
  '\u{2764}',   // ❤ red heart
  '\u{1F499}', // 💙 blue heart
  '\u{1F49A}', // 💚 green heart
  '\u{1F49B}', // 💛 yellow heart
  '\u{1F49C}', // 💜 purple heart
];

/**
 * Icon Picker — grid of emoji options.
 *
 * Props:
 *  - selected  : string — currently selected emoji (empty = none)
 *  - onSelect  : (icon: string) => void
 *  - onClear   : () => void — called when "none" is clicked
 */
export default function IconPicker({ selected, onSelect, onClear }) {
  const [hovered, setHovered] = useState(null);
  const [tab, setTab] = useState('emoji');

  // Resolve selected icon for preview display
  const isFeatherSelected = selected && selected.startsWith('feather:');
  const selectedFeatherName = isFeatherSelected ? selected.slice(8) : '';
  const SelectedFeatherIcon = isFeatherSelected ? FEATHER_MAP[selectedFeatherName] : null;

  return (
    <div>
      {/* Currently selected */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-sm)',
        marginBottom: 'var(--space-sm)',
        fontSize: 'var(--text-sm)',
      }}>
        {selected ? (
          <>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: isFeatherSelected ? undefined : '1.25rem',
              color: isFeatherSelected ? 'var(--ink)' : undefined,
            }}>
              {isFeatherSelected && SelectedFeatherIcon
                ? <SelectedFeatherIcon size={20} strokeWidth={2} />
                : selected}
            </span>
            <button
              className="btn btn-flat"
              onClick={onClear}
              style={{
                fontSize: 'var(--text-xs)',
                padding: '2px 10px',
              }}
            >
              Clear
            </button>
          </>
        ) : (
          <span style={{ color: 'var(--ink-muted)', fontStyle: 'italic' }}>
            using default icon
          </span>
        )}
      </div>

      {/* Tab switcher */}
      <div style={{
        display: 'flex',
        gap: 0,
        marginBottom: 'var(--space-xs)',
        borderBottom: 'var(--border-thin)',
      }}>
        <button
          onClick={() => setTab('emoji')}
          style={{
            padding: 'var(--space-xs) var(--space-sm)',
            border: 'none',
            borderBottom: tab === 'emoji' ? '2px solid var(--accent)' : '2px solid transparent',
            background: 'transparent',
            color: tab === 'emoji' ? 'var(--ink)' : 'var(--ink-muted)',
            cursor: 'pointer',
            fontSize: 'var(--text-xs)',
            fontWeight: tab === 'emoji' ? 'var(--weight-semibold)' : undefined,
          }}
        >
          Emoji
        </button>
        <button
          onClick={() => setTab('feather')}
          style={{
            padding: 'var(--space-xs) var(--space-sm)',
            border: 'none',
            borderBottom: tab === 'feather' ? '2px solid var(--accent)' : '2px solid transparent',
            background: 'transparent',
            color: tab === 'feather' ? 'var(--ink)' : 'var(--ink-muted)',
            cursor: 'pointer',
            fontSize: 'var(--text-xs)',
            fontWeight: tab === 'feather' ? 'var(--weight-semibold)' : undefined,
          }}
        >
          Feather
        </button>
      </div>

      {/* Icon grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(3rem, 1fr))',
        gap: '6px',
        maxHeight: 240,
        overflowY: 'auto',
        padding: 'var(--space-xs)',
        border: 'var(--border-thin)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--paper-stack)',
      }}>
        {tab === 'emoji' ? (
          ICON_OPTIONS.map(icon => {
            const isSelected = selected === icon;
            const isHovered = hovered === icon;
            return (
              <button
                key={icon}
                onClick={() => onSelect(icon)}
                onMouseEnter={() => setHovered(icon)}
                onMouseLeave={() => setHovered(null)}
                title={icon}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  aspectRatio: '1',
                  border: isSelected
                    ? '2px solid var(--accent)'
                    : '1px solid transparent',
                  borderRadius: 'var(--radius-sm)',
                  background: isSelected
                    ? 'rgba(var(--accent-rgb), 0.12)'
                    : isHovered
                      ? 'var(--rule)'
                      : 'transparent',
                  cursor: 'pointer',
                  fontSize: '1.55rem',
                  padding: 0,
                  transition: 'all 0.12s ease-out',
                }}
              >
                {icon}
              </button>
            );
          })
        ) : (
          FEATHER_OPTIONS.map(({ name, Icon }) => {
            const featherKey = `feather:${name}`;
            const isSelected = selected === featherKey;
            const isHovered = hovered === name;
            return (
              <button
                key={name}
                onClick={() => onSelect(featherKey)}
                onMouseEnter={() => setHovered(name)}
                onMouseLeave={() => setHovered(null)}
                title={name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  aspectRatio: '1',
                  border: isSelected
                    ? '2px solid var(--accent)'
                    : '1px solid transparent',
                  borderRadius: 'var(--radius-sm)',
                  background: isSelected
                    ? 'rgba(var(--accent-rgb), 0.12)'
                    : isHovered
                      ? 'var(--rule)'
                      : 'transparent',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'all 0.12s ease-out',
                  color: 'var(--ink)',
                }}
              >
                <Icon size={20} strokeWidth={2} />
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

export { ICON_OPTIONS };
